import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import { useTranslation } from 'react-i18next';
import './Integration.css';

type ValueType = 'string' | 'bool' | 'int';

interface IntegrationDto {
  id: number;
  code: string;
  name: string;
}

interface IntegrationSettingDto {
  id: number;
  integrationId: number;
  key: string;
  value: string | null;
  valueType: ValueType | string;
  isSecret: boolean;
}

interface EditableSetting extends IntegrationSettingDto {
  label: string;
  hasStoredSecret: boolean;
  isNew?: boolean;
}

interface EbillsFieldDef {
  key: string;
  label: string;
  valueType: ValueType;
  isSecret: boolean;
  placeholder?: string;
}

const EBILLS_FIELDS: EbillsFieldDef[] = [
  { key: 'BaseUrl', label: 'Base URL', valueType: 'string', isSecret: false, placeholder: 'https://...' },
  { key: 'Token', label: 'Token', valueType: 'string', isSecret: true, placeholder: 'Enter token' },
  { key: 'UseBearer', label: 'Use Bearer', valueType: 'bool', isSecret: false },
  { key: 'IsCloud', label: 'Is Cloud', valueType: 'bool', isSecret: false },
  { key: 'NiptId', label: 'NIPT ID', valueType: 'string', isSecret: false },
  { key: 'OperatorCode', label: 'Operator Code', valueType: 'string', isSecret: false },
  { key: 'PosId', label: 'POS ID', valueType: 'int', isSecret: false },
  { key: 'PricesIncludeVat', label: 'Prices Include VAT', valueType: 'bool', isSecret: false },
];

const toBool = (value?: string | null) => {
  const v = (value ?? '').trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
};

const toValueType = (valueType?: string): ValueType => {
  const normalized = (valueType ?? '').trim().toLowerCase();
  if (normalized === 'bool' || normalized === 'boolean') return 'bool';
  if (normalized === 'int' || normalized === 'number') return 'int';
  return 'string';
};

const prettifyKey = (key: string) =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

const isEbills = (code?: string) => (code ?? '').trim().toUpperCase() === 'EBILLS';
const sanitizeIntegrationCode = (code?: string) => {
  const value = (code ?? '').trim();
  if (!value) return '';
  const last = value.split('/').filter(Boolean).pop() ?? value;
  return last.trim();
};
const resolveSettingsByIntegrationIdUrl = (integrationId: number) => {
  const fromConst = ApiConstants.integrationSettings.byIntegrationId(integrationId);
  if (typeof fromConst === 'string' && fromConst.trim().length > 0) {
    if (fromConst.startsWith('/api/')) return fromConst;
    if (fromConst.startsWith('api/')) return `/${fromConst}`;
  }
  return `/api/integration-settings/list?integrationId=${integrationId}`;
};

const normalizeSettings = (integrationCode: string, settings: IntegrationSettingDto[]) => {
  const byKey = new Map(settings.map((item) => [item.key.toLowerCase(), item]));
  const normalizedServer = settings.map<EditableSetting>((item) => ({
    ...item,
    valueType: toValueType(item.valueType),
    label: prettifyKey(item.key),
    hasStoredSecret: item.isSecret && item.value == null,
  }));

  if (!isEbills(integrationCode)) {
    return normalizedServer;
  }

  const defaults = EBILLS_FIELDS.map<EditableSetting>((field) => {
    const existing = byKey.get(field.key.toLowerCase());
    if (existing) {
      return {
        ...existing,
        valueType: toValueType(existing.valueType),
        label: field.label,
        hasStoredSecret: existing.isSecret && existing.value == null,
      };
    }

    return {
      id: 0,
      integrationId: existing?.integrationId ?? 0,
      key: field.key,
      value: '',
      valueType: field.valueType,
      isSecret: field.isSecret,
      label: field.label,
      hasStoredSecret: false,
    };
  });

  const extra = normalizedServer.filter((row) => !EBILLS_FIELDS.some((f) => f.key.toLowerCase() === row.key.toLowerCase()));
  return [...defaults, ...extra];
};

const normalizeForCompare = (row: EditableSetting, value: string | null) => {
  const vt = toValueType(row.valueType);
  if (vt === 'bool') return toBool(value) ? 'true' : 'false';
  if (vt === 'int') return (value ?? '').trim();
  return value ?? '';
};

const IntegrationSettings: React.FC = () => {
  const { i18n } = useTranslation();
  const tx = (key: string, fallback: string) => i18n.t(`integration.${key}`, { defaultValue: fallback });
  const fieldLabel = (key: string, fallback: string) => {
    const translated = i18n.t(`integration.fields.${key}`, { defaultValue: fallback });
    return `${translated} (${key})`;
  };

  const [integrations, setIntegrations] = useState<IntegrationDto[]>([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<number>(0);
  const [settings, setSettings] = useState<EditableSetting[]>([]);
  const [formValues, setFormValues] = useState<Record<string, string | boolean>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingField, setIsAddingField] = useState(false);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldType, setNewFieldType] = useState<ValueType>('string');
  const [newFieldSecret, setNewFieldSecret] = useState(false);
  const [newFieldValue, setNewFieldValue] = useState('');
  const [isAddingIntegration, setIsAddingIntegration] = useState(false);
  const [isCreatingIntegration, setIsCreatingIntegration] = useState(false);
  const [newIntegrationCode, setNewIntegrationCode] = useState('');
  const [newIntegrationName, setNewIntegrationName] = useState('');

  const selectedIntegration = useMemo(() => integrations.find((x) => x.id === selectedIntegrationId), [integrations, selectedIntegrationId]);

  const filteredIntegrations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return integrations;
    return integrations.filter((x) => `${x.name} ${x.code}`.toLowerCase().includes(q));
  }, [integrations, searchTerm]);

  const loadIntegrations = async (preferred?: { id?: number; code?: string }) => {
    setIsLoadingIntegrations(true);
    try {
      const res = await axiosInstance.get<IntegrationDto[]>(ApiConstants.integrations.list);
      const list = res.data ?? [];
      setIntegrations(list);

      if (!list.length) {
        setSelectedIntegrationId(0);
        setSettings([]);
        setFormValues({});
        return;
      }

      setSelectedIntegrationId((prev) => {
        if (preferred?.id) {
          const preferredById = list.find((x) => x.id === preferred.id);
          if (preferredById) return preferredById.id;
        }

        const normalizedPreferred = sanitizeIntegrationCode(preferred?.code).toUpperCase();
        if (normalizedPreferred) {
          const preferred = list.find((x) => sanitizeIntegrationCode(x.code).toUpperCase() === normalizedPreferred);
          if (preferred) return preferred.id;
        }

        if (prev) {
          const existing = list.find((x) => x.id === prev);
          if (existing) return existing.id;
        }

        return list[0].id;
      });
    } catch (error) {
      console.error(error);
      toast.error(i18n.t('generic.somethingWrong', { defaultValue: 'Something went wrong' }));
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  const loadSettings = async (integration: IntegrationDto | undefined) => {
    if (!integration?.id) return;

    setIsLoadingSettings(true);
    try {
      const res = await axiosInstance.get<IntegrationSettingDto[]>(resolveSettingsByIntegrationIdUrl(integration.id));
      const rows = normalizeSettings(integration.code, res.data ?? []);
      setSettings(rows);

      const nextValues: Record<string, string | boolean> = {};
      rows.forEach((row) => {
        const vt = toValueType(row.valueType);
        if (vt === 'bool') {
          nextValues[row.key] = toBool(row.value);
          return;
        }
        nextValues[row.key] = row.value ?? '';
      });
      setFormValues(nextValues);
    } catch (error) {
      console.error(error);
      console.error('Integration settings load failed for integration:', integration.id);
      toast.error(i18n.t('generic.somethingWrong', { defaultValue: 'Something went wrong' }));
    } finally {
      setIsLoadingSettings(false);
    }
  };

  useEffect(() => {
    loadIntegrations();
  }, []);

  useEffect(() => {
    if (selectedIntegration) {
      loadSettings(selectedIntegration);
    }
  }, [selectedIntegration]);

  const onSave = async () => {
    if (!selectedIntegration?.id) return;

    const requests = settings.flatMap((row) => {
      const current = formValues[row.key];
      const vt = toValueType(row.valueType);

      if (row.isSecret && row.hasStoredSecret && (!current || String(current).trim() === '')) {
        return [];
      }

      let value: string | null = null;
      if (vt === 'bool') {
        value = String(Boolean(current));
      } else {
        const raw = String(current ?? '').trim();
        value = raw.length ? raw : null;
      }

      const oldNorm = normalizeForCompare(row, row.value);
      const newNorm = normalizeForCompare(row, value);

      if (!row.isNew && oldNorm === newNorm && !(row.isSecret && row.hasStoredSecret)) {
        return [];
      }

      return [
        {
          rowId: row.id,
          payload: {
            integrationId: selectedIntegration.id,
            key: row.key,
            value,
            valueType: vt,
            isSecret: row.isSecret,
          },
        },
      ];
    });

    if (!requests.length) {
      toast.info(i18n.t('generic.noData', { defaultValue: 'No data to save' }));
      return;
    }

    setIsSaving(true);
    try {
      await axiosInstance.post(ApiConstants.integrationSettings.saveAll, requests.map((req) => req.payload));
      toast.success(tx('savedSuccessfully', 'Saved successfully'));
      await loadSettings(selectedIntegration);
    } catch (error) {
      console.error(error);
      toast.error(tx('errorSaving', 'Error while saving'));
    } finally {
      setIsSaving(false);
    }
  };

  const onReload = async () => {
    if (!selectedIntegration) return;
    await loadSettings(selectedIntegration);
  };

  const onAddField = () => {
    const key = newFieldKey.trim();
    if (!key) {
      toast.error(tx('fieldKeyRequired', 'Field key is required'));
      return;
    }

    if (settings.some((x) => x.key.trim().toLowerCase() === key.toLowerCase())) {
      toast.error(tx('fieldAlreadyExists', 'Field already exists'));
      return;
    }

    const setting: EditableSetting = {
      id: 0,
      integrationId: selectedIntegration?.id ?? 0,
      key,
      value: newFieldType === 'bool' ? 'false' : newFieldValue.trim() || null,
      valueType: newFieldType,
      isSecret: newFieldSecret,
      label: prettifyKey(key),
      hasStoredSecret: false,
      isNew: true,
    };

    setSettings((prev) => [...prev, setting]);
    setFormValues((prev) => ({
      ...prev,
      [key]: newFieldType === 'bool' ? false : newFieldValue,
    }));

    setIsAddingField(false);
    setNewFieldKey('');
    setNewFieldType('string');
    setNewFieldSecret(false);
    setNewFieldValue('');
  };

  const onRemoveNewField = (key: string) => {
    setSettings((prev) => prev.filter((x) => x.key !== key));
    setFormValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const onAddIntegration = async () => {
    const code = sanitizeIntegrationCode(newIntegrationCode).toUpperCase();
    const name = newIntegrationName.trim();

    if (!code) {
      toast.error(tx('integrationCodeRequired', 'Integration code is required'));
      return;
    }

    if (!name) {
      toast.error(tx('integrationNameRequired', 'Integration name is required'));
      return;
    }

    setIsCreatingIntegration(true);
    try {
      await axiosInstance.post(ApiConstants.integrations.add, { code, name });
      await loadIntegrations({ code });
      setIsAddingIntegration(false);
      setNewIntegrationCode('');
      setNewIntegrationName('');
      setSearchTerm('');
      toast.success(tx('integrationAdded', 'Integration added successfully'));
    } catch (error) {
      console.error(error);
      const status = (error as any)?.response?.status;
      if (status === 404) {
        toast.error(tx('integrationEndpointMissing', 'Add integration endpoint not found. Restart backend and verify route.'));
      } else {
        toast.error(tx('integrationAddFailed', 'Failed to add integration'));
      }
    } finally {
      setIsCreatingIntegration(false);
    }
  };

  return (
    <div className="integration-wrap">
      {/* Header with gradient background */}
      <div className="integration-header">
        <div className="integration-header-content">
          <div className="integration-title-section">
            <h1 className="integration-title">
              <span className="integration-title-highlight">{tx('title', 'Integration')}</span>
            </h1>
          </div>
          <div className="integration-header-decoration">
            <div className="integration-header-shape"></div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="integration-content">
        {/* Sidebar with integration list */}
        <aside className="integration-sidebar">
          <div className="integration-sidebar-header">
            <div className="integration-sidebar-title-wrapper">
              <h2 className="integration-sidebar-title">{tx('integrations', 'Integrations')}</h2>
              <span className="integration-badge">{filteredIntegrations.length}</span>
            </div>
            <div className="integration-sidebar-search">
              <svg className="integration-search-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                type="text"
                placeholder={tx('search', 'Search')}
                className="integration-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="integration-button secondary integration-add-integration-toggle"
              onClick={() => setIsAddingIntegration((prev) => !prev)}
              disabled={isLoadingIntegrations || isCreatingIntegration}
            >
              <span>{isAddingIntegration ? tx('cancel', 'Cancel') : tx('addIntegration', 'Add Integration')}</span>
            </button>
            {isAddingIntegration && (
              <div className="integration-add-integration-form">
                <input
                  className="integration-input"
                  value={newIntegrationCode}
                  onChange={(e) => setNewIntegrationCode(e.target.value)}
                  placeholder={tx('integrationCode', 'Code (e.g. MYFISCAL)')}
                />
                <input
                  className="integration-input"
                  value={newIntegrationName}
                  onChange={(e) => setNewIntegrationName(e.target.value)}
                  placeholder={tx('integrationName', 'Name (e.g. My Fiscal Company)')}
                />
                <button type="button" className="integration-button primary" onClick={onAddIntegration} disabled={isCreatingIntegration}>
                  <span>{isCreatingIntegration ? tx('saving', 'Saving...') : tx('add', 'Add')}</span>
                </button>
              </div>
            )}
          </div>

          <div className="integration-list">
            {isLoadingIntegrations ? (
              <div className="integration-loading">
                <div className="integration-loading-spinner"></div>
                <span>{tx('loadingIntegrations', 'Loading integrations...')}</span>
              </div>
            ) : filteredIntegrations.length === 0 ? (
              <div className="integration-empty">
                <svg className="integration-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p>{tx('noIntegrationsFound', 'No integrations found')}</p>
              </div>
            ) : (
              filteredIntegrations.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`integration-item ${item.id === selectedIntegrationId ? 'is-active' : ''}`}
                  onClick={() => setSelectedIntegrationId(item.id)}
                >
                  <div className="integration-item-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="integration-item-content">
                    <span className="integration-item-name">{item.name}</span>
                    <span className="integration-item-code">{item.code}</span>
                  </div>
                  {item.id === selectedIntegrationId && <div className="integration-item-active-indicator"></div>}
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Main panel with settings */}
        <main className="integration-panel">
          <div className="integration-panel-header">
            <div className="integration-panel-title-wrapper">
              <h2 className="integration-panel-title">{selectedIntegration?.name ?? tx('integrationSettings', 'Integration Settings')}</h2>
              <span className="integration-panel-subtitle">
                {settings.length}{' '}
                {settings.length === 1 ? tx('configurationOption', 'configuration option') : tx('configurationOptions', 'configuration options')}
              </span>
            </div>
            <div className="integration-panel-actions">
              <button
                type="button"
                className="integration-button secondary"
                onClick={() => setIsAddingField((prev) => !prev)}
                disabled={!selectedIntegrationId || isLoadingSettings || isSaving}
              >
                <span>{isAddingField ? tx('cancel', 'Cancel') : tx('addSetting', 'Add Setting')}</span>
              </button>
              <select
                className="integration-select"
                value={selectedIntegrationId}
                onChange={(e) => setSelectedIntegrationId(Number(e.target.value) || 0)}
                disabled={isLoadingIntegrations || integrations.length === 0}
              >
                {integrations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} ({item.code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="integration-settings">
            {isAddingField && (
              <div className="integration-add-form">
                <div className="integration-add-grid">
                  <input
                    className="integration-input"
                    value={newFieldKey}
                    onChange={(e) => setNewFieldKey(e.target.value)}
                    placeholder={tx('fieldKey', 'Field key (e.g. ApiKey)')}
                  />
                  <select
                    className="integration-select integration-add-select"
                    value={newFieldType}
                    onChange={(e) => setNewFieldType(e.target.value as ValueType)}
                  >
                    <option value="string">{tx('types.string', 'string')}</option>
                    <option value="int">{tx('types.int', 'int')}</option>
                    <option value="bool">{tx('types.bool', 'bool')}</option>
                  </select>
                  <label className="integration-add-secret">
                    <input type="checkbox" checked={newFieldSecret} onChange={(e) => setNewFieldSecret(e.target.checked)} />
                    <span>{tx('secret', 'secret')}</span>
                  </label>
                  {newFieldType !== 'bool' && (
                    <input
                      className="integration-input"
                      value={newFieldValue}
                      onChange={(e) => setNewFieldValue(e.target.value)}
                      placeholder={tx('fieldValue', 'Initial value (optional)')}
                      type={newFieldType === 'int' ? 'number' : newFieldSecret ? 'password' : 'text'}
                    />
                  )}
                  <button type="button" className="integration-button primary" onClick={onAddField}>
                    <span>{tx('add', 'Add')}</span>
                  </button>
                </div>
              </div>
            )}

            {isLoadingSettings ? (
              <div className="integration-loading-settings">
                <div className="integration-loading-spinner"></div>
                <span>{tx('loadingSettings', 'Loading settings...')}</span>
              </div>
            ) : !settings.length ? (
              <div className="integration-empty-settings">
                <svg className="integration-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p>{tx('noSettingsAvailable', 'No settings available')}</p>
              </div>
            ) : (
              <div className="integration-settings-grid">
                {settings.map((row) => {
                  const valueType = toValueType(row.valueType);
                  const ebillsDef = EBILLS_FIELDS.find((x) => x.key.toLowerCase() === row.key.toLowerCase());
                  const placeholder =
                    row.isSecret && row.hasStoredSecret ? tx('savedMasked', '********') : ebillsDef?.placeholder || tx('enterField', 'Enter value');

                  return (
                    <div className="integration-field" key={row.key}>
                      <div className="integration-field-header">
                        <label className="integration-field-label">{fieldLabel(row.key, row.label)}</label>
                        <div className="integration-field-badges">
                          <span className="integration-type-badge">{tx(`types.${valueType}`, valueType)}</span>
                          {row.isSecret && (
                            <span className="integration-type-badge secret">
                              <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                <path
                                  fillRule="evenodd"
                                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {tx('secret', 'secret')}
                            </span>
                          )}
                          {row.isNew && (
                            <button
                              type="button"
                              className="integration-remove-new"
                              onClick={() => onRemoveNewField(row.key)}
                              title={tx('remove', 'Remove')}
                            >
                              {tx('remove', 'Remove')}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="integration-field-control">
                        {valueType === 'bool' ? (
                          <label className="integration-toggle">
                            <input
                              type="checkbox"
                              checked={Boolean(formValues[row.key])}
                              onChange={(e) => setFormValues((prev) => ({ ...prev, [row.key]: e.target.checked }))}
                            />
                            <span className="integration-toggle-slider"></span>
                            <span className="integration-toggle-label">
                              {Boolean(formValues[row.key]) ? tx('enabled', 'Enabled') : tx('disabled', 'Disabled')}
                            </span>
                          </label>
                        ) : (
                          <div className="integration-input-wrapper">
                            <input
                              type={row.isSecret ? 'password' : valueType === 'int' ? 'number' : 'text'}
                              value={String(formValues[row.key] ?? '')}
                              placeholder={placeholder}
                              onChange={(e) => setFormValues((prev) => ({ ...prev, [row.key]: e.target.value }))}
                              className="integration-input"
                            />
                            {row.hasStoredSecret && <span className="integration-input-hint">{tx('saved', 'Saved')}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="integration-footer">
              <button type="button" className="integration-button secondary" onClick={onReload} disabled={isLoadingSettings || !selectedIntegrationId}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                  <path
                    fillRule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{tx('reload', 'Reload')}</span>
              </button>
              <button type="button" className="integration-button primary" onClick={onSave} disabled={isSaving || isLoadingSettings || !selectedIntegrationId}>
                {isSaving ? (
                  <>
                    <div className="integration-button-spinner"></div>
                    <span>{tx('saving', 'Saving...')}</span>
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                      <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                    </svg>
                    <span>{tx('saveChanges', 'Save Changes')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default IntegrationSettings;
